import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import classes from './LendScreen.module.css';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../config';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const LoadingScreen = () => (
  <div className={classes.loadingScreen}>
    <div className={classes.spinner} />
    <p className={classes.loadingText}>Loading...</p>
  </div>
);

const LendScreen = ({ groupId }) => {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState({});
  const [showNameSheet, setShowNameSheet] = useState(false);
  const [lenderName, setLenderName] = useState('');
  const [working, setWorking] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!groupId) { setLoading(false); return; }
    const unsubscribe = onSnapshot(
      doc(db, 'groups', groupId),
      (snap) => {
        if (snap.exists()) {
          setGroup(snap.data());
        } else {
          setGroup(null);
        }
        setLoading(false);
      },
      (err) => {
        console.log('Firestore error:', err);
        setLoadError(true);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [groupId]);

  if (loading) return <LoadingScreen />;

  if (loadError || !group) {
    return (
      <div className={classes.errorScreen}>
        <p className={classes.errorText}>
          {loadError
            ? 'Unable to load this page. The link may have expired.'
            : 'Group not found.'}
        </p>
      </div>
    );
  }

  const borrowerName = group.members?.find(m => m.owner)?.name || group.members?.[0]?.name || 'Your mate';
  const borrowerFirstName = borrowerName.split(' ')[0];
  const festivalName = group.festival?.name || 'the festival';
  const startDate = formatDate(group.festival?.startdate);
  const endDate = formatDate(group.festival?.enddate);
  const dateRange = startDate && endDate ? `${startDate} to ${endDate}` : startDate || endDate || '';

  const borrowItems = (group.items || []).filter(item => {
    const qty = item?.needQuantity !== undefined
      ? item.needQuantity
      : (item?.organise || []).filter(o => o.method === 'Borrow' || o.method === 'Hire').length;
    return qty > 0 && item.borrowStatus !== 'offered';
  });

  const toggleSelect = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const handleConfirmLend = async () => {
    if (!lenderName.trim() || working) return;
    setWorking(true);
    try {
      const updatedItems = (group.items || []).map(item =>
        selectedIds.includes(item.id)
          // lentBy persists past the point offeredBy gets stripped (see
          // NeedScreen.js's commitMoveToPack / ItemDetail.js's handleGotIt/
          // handleClearStatus) — it's what lets Pack still show who lent
          // an item once it's been sorted. Deliberately a new field, not
          // the existing (unused) item.lenderName.
          ? { ...item, borrowStatus: 'offered', offeredBy: lenderName.trim(), lentBy: lenderName.trim() }
          : item
      );
      await updateDoc(doc(db, 'groups', groupId), { items: updatedItems });
      setSuccess(true);
    } catch (err) {
      console.log('Error confirming lend:', err);
    }
    setWorking(false);
    setShowNameSheet(false);
  };

  if (success) {
    return (
      <div className={classes.successScreen}>
        <span className={classes.partyEmoji}>🎉</span>
        <h1 className={classes.successTitle}>Thanks!</h1>
        <p className={classes.successMsg}>
          We'll let {borrowerFirstName} know!
        </p>
        <p className={classes.successSubMsg}>You can close this window now.</p>
        <button className={classes.gotItBtn} onClick={() => window.close()}>Got it!</button>
        <p className={classes.poweredBy}>Powered by Camping Buddy</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>

      {/* Header */}
      <div className={classes.header}>
        <h1 className={classes.headerTitle}>Help a mate out! 🤝</h1>
        <p className={classes.subtitle}>
          <strong>{borrowerName}</strong> is going to <strong>{festivalName}</strong>
          {dateRange ? ` from ${dateRange}` : ''} and needs to borrow some gear. Can you help?
        </p>
      </div>

      {/* Items */}
      <div className={classes.content}>
        <div className={classes.itemsCard}>
          {borrowItems.length === 0 ? (
            <p className={classes.noItems}>All items have been sorted! 🎉</p>
          ) : (
            borrowItems.map((item, i) => {
              const qty = item.needQuantity || 1;
              const isSelected = !!selected[item.id];
              return (
                <div key={item.id} className={`${classes.itemRow} ${i > 0 ? classes.itemRowBorder : ''}`}>
                  <div className={classes.itemInfo}>
                    <span className={classes.itemName}>{item.name}</span>
                    {qty > 1 && <span className={classes.itemQty}> ×{qty}</span>}
                  </div>
                  <button
                    className={`${classes.lendBtn} ${isSelected ? classes.lendBtnSelected : ''}`}
                    onClick={() => toggleSelect(item.id)}
                  >
                    {isSelected ? '✓ I can lend this' : 'Lend this'}
                  </button>
                </div>
              );
            })
          )}
        </div>
        <p className={classes.poweredByInline}>Powered by Camping Buddy</p>
      </div>

      {/* Sticky footer — always visible, disabled until something selected */}
      <div className={classes.footer}>
        <button
          className={classes.lendAllBtn}
          onClick={() => setShowNameSheet(true)}
          disabled={selectedIds.length === 0}
        >
          {selectedIds.length > 0
            ? `Lend ${selectedIds.length} item${selectedIds.length !== 1 ? 's' : ''}`
            : 'Lend items'}
        </button>
      </div>

      {/* Add name sheet */}
      {showNameSheet && ReactDOM.createPortal(
        <>
          <div className={classes.backdrop} onClick={() => setShowNameSheet(false)} />
          <div className={classes.sheet}>
            <h3 className={classes.sheetTitle}>Add your name</h3>
            <input
              className={classes.nameInput}
              type="text"
              placeholder="Your name"
              value={lenderName}
              onChange={e => setLenderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleConfirmLend()}
              autoFocus
            />
            <button
              className={classes.confirmBtn}
              onClick={handleConfirmLend}
              disabled={!lenderName.trim() || working}
            >
              {working ? 'Confirming...' : 'Confirm'}
            </button>
          </div>
        </>,
        document.body
      )}

    </div>
  );
};

export default LendScreen;
